import React, { useEffect, useMemo, useState } from 'react'
import { Button, Table, Typography } from 'antd'
import Highlighter from 'react-highlight-words'
import { useDispatch, useSelector } from 'react-redux'
import findIndex from 'lodash/findIndex'
import get from 'lodash/get'

import { useNavigate, useSearchParams } from 'react-router-dom'
import { setApplicationItem } from '../../../store/application'
import { userSelector } from '../../../store/session'
import useTableFilter from '../../hooks/useTableFilter'
import { calcSumAmount, calcSumCorrectionAmount } from '../../../utils/calculation'
import { getOffBudget } from '../app-view-utils'
import { formatDecimalAmount, formatMoney } from '../../../utils/number-utils'

const { Text } = Typography

const getVatType = (vatType) => {
  switch (vatType) {
    case 'none':
      return 'Без НДС'
    case 'up':
      return 'НДС сверху'
    case 'include':
      return 'НДС в сумме'
    default:
      return ''
  }
}

const numberCellStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  overflowWrap: 'normal',
}

const ImplementationItemsTable = ({ storedData, applicationRecord, filter, tableId }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useSelector(userSelector)
  const [filteredColumns, setFilteredColumns] = useState([])
  const [tableFilter, searchText, searchedColumn, getColumnSearchProps] = useTableFilter()
  const getApplicationItem = (record) =>
    applicationRecord?.application_items?.find((value) => value?.id === record?.application_item?.id)
  const isProject = useMemo(() => user?.role?.type === 'project_manager', [user])

  const onUpdateHandler = (applicationItem) => {
    dispatch(setApplicationItem({ applicationItem }))
    navigate(`/application/new?state=${searchParams.get('state')}`)
  }

  const columns = [
    {
      title: 'Наименование',
      dataIndex: 'name',
      key: 'name',
      render: (name) => <Highlighter searchWords={[filter?.name]} autoEscape textToHighlight={name} />,
    },
    {
      title: 'Кол-во заявки (снабжение)',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => <div style={numberCellStyle}>{formatDecimalAmount(amount)}</div>,
    },
    {
      title: 'Кол-во фактическое',
      dataIndex: 'actualAmount',
      key: 'actualAmount',
      render: (actualAmount, record) => (
        <div style={numberCellStyle}>{formatDecimalAmount(calcSumCorrectionAmount(record))}</div>
      ),
    },
    {
      title: 'Возврат',
      dataIndex: 'refund_items',
      key: 'refund_items',
      render: (refund_items) => (
        <div style={numberCellStyle}>
          {formatDecimalAmount(refund_items?.reduce((acc, refund) => acc + refund.amount, 0) || 0)}
        </div>
      ),
    },
    {
      title: 'Кор-ка кол-ва',
      dataIndex: 'correctedAmount',
      key: 'correctedAmount',
      render: (correctedAmount) => <div style={numberCellStyle}>{formatDecimalAmount(correctedAmount)}</div>,
    },
    {
      title: 'Цена за единицу',
      dataIndex: 'price',
      key: 'price',
      render: (price) => formatMoney(price),
    },
    {
      title: 'Сумма',
      dataIndex: 'price',
      key: 'sum',
      render: (price, record) => {
        const finalAmount = calcSumAmount(record)
        if (price >= 0 && finalAmount >= 0) {
          return formatMoney(price * finalAmount)
        }
      },
    },
    {
      title: 'Тип НДС',
      dataIndex: 'vatType',
      key: 'vatType',
      render: (vatType) => getVatType(vatType),
    },
    {
      title: '% НДС',
      dataIndex: 'vatValue',
      key: 'vatValue',
      render: (vatValue) => (vatValue ? `${vatValue} %` : ''),
    },
    {
      title: 'НДС',
      dataIndex: 'vatValue',
      key: 'vatCalc',
      render: (vatValue, record) => {
        const finalAmount = calcSumAmount(record)
        if (record.vatType === 'up' && finalAmount >= 0 && vatValue > 0 && record.price > 0) {
          const sum = record.price * finalAmount
          const vat = (sum / 100) * vatValue
          return formatMoney(vat)
        } else if (record.vatType === 'include' && finalAmount >= 0 && vatValue > 0 && record.price > 0) {
          const sum = record.price * finalAmount
          const vat = (sum * vatValue) / (100 + vatValue)
          return formatMoney(vat)
        }
        return ''
      },
    },
    {
      title: 'Всего с НДС',
      dataIndex: 'vatValue',
      key: 'vatSum',
      render: (vatValue, record) => {
        const finalAmount = calcSumAmount(record)
        if (record.vatType === 'up' && vatValue > 0 && record.price >= 0 && finalAmount >= 0) {
          const sum = record.price * finalAmount
          const vat = (sum / 100) * vatValue
          return formatMoney(sum + vat)
        } else if (record.vatType === 'include' && record.price >= 0 && finalAmount >= 0) {
          return formatMoney(record.price * finalAmount)
        }
        return ''
      },
    },
    {
      title: 'Ед.изм.',
      dataIndex: 'unit',
      key: 'unit',
      render: (unit) => unit,
    },
    {
      title: 'Поставщик',
      dataIndex: 'provider',
      key: 'provider',
      ...getColumnSearchProps('provider'),
      onFilter: (value, record) => get(record, 'provider')?.toString()?.toLowerCase()?.includes(value?.toLowerCase()),
      render: (provider, record) => {
        const toDisplay = `${provider ? `${provider}` : ''} ${record?.warehouse ? ` Склад: ${record.warehouse}` : ''}`
        return searchedColumn === 'provider' ? (
          <Highlighter searchWords={[searchText]} autoEscape textToHighlight={toDisplay} />
        ) : (
          toDisplay
        )
      },
    },
    {
      title: 'Комментарий',
      dataIndex: 'description',
      key: 'description',
      render: (description) => description,
    },
    {
      title: 'Статус',
      dataIndex: 'state',
      key: 'state',
      render: (_, record) => {
        const applicationItem = getApplicationItem(record)

        if (applicationItem) {
          return `${
            applicationItem?.approved
              ? 'Согласована'
              : `Не согласована ${
                  applicationItem.approved_comment ? ` (Примечание: ${applicationItem.approved_comment})` : ''
                }`
          }`
        }

        return null
      },
    },
    {
      title: 'Наименование заявка',
      dataIndex: 'application_name',
      key: 'application_name',
      render: (_, record) => {
        const applicationItem = getApplicationItem(record)
        if (applicationItem) {
          return <Highlighter searchWords={[filter?.name]} autoEscape textToHighlight={applicationItem.name} />
        }
        return null
      },
    },
    {
      title: 'Кол-во заявка (прораб)',
      dataIndex: 'application_amount',
      className: 'no-padding-cell-report',
      key: 'application_amount',
      render: (_, record) => {
        const applicationItem = getApplicationItem(record)

        if (applicationItem) {
          const estimateAmount = applicationItem?.estimate_field?.amount
          const isOver = estimateAmount < applicationItem?.amount
          return (
            <div
              style={{
                backgroundColor: isOver ? '#eca8b2' : 'white',
                height: '100%',
                padding: 8,
                display: 'flex',
                alignItems: 'center',
              }}
            >{`${formatDecimalAmount(applicationItem?.amount)} ${applicationItem.unit}`}</div>
          )
        }
        return null
      },
    },
    {
      title: 'Комментарий заявка',
      dataIndex: 'application_description',
      key: 'application_description',
      render: (_, record) => {
        const applicationItem = getApplicationItem(record)
        if (applicationItem) {
          return applicationItem.description
        }
        return null
      },
    },
    {
      title: 'Наименование смета',
      dataIndex: 'estimate_name',
      key: 'estimate_name',
      render: (_, record) => {
        const applicationItem = getApplicationItem(record)
        if (applicationItem) {
          return (
            <Highlighter
              searchWords={[filter?.name]}
              autoEscape
              textToHighlight={applicationItem?.estimate_field?.name}
            />
          )
        }
        return null
      },
    },
    {
      title: 'Категория смета',
      dataIndex: 'estimate_category',
      key: 'estimate_category',
      render: (_, record) => {
        const applicationItem = getApplicationItem(record)
        if (applicationItem) {
          return (
            <Highlighter
              searchWords={filter?.categories || []}
              autoEscape
              textToHighlight={applicationItem?.estimate_field?.category || applicationItem?.category}
            />
          )
        }
        return null
      },
    },
    {
      title: 'Вне сметы',
      dataIndex: 'off_budget',
      key: 'off_budget',
      render: (_, record) => {
        const applicationItem = getApplicationItem(record)
        return applicationItem ? getOffBudget(applicationItem.off_budget) : ''
      },
    },
    {
      title: 'Цена смета без НДС',
      dataIndex: 'estimate_price',
      key: 'estimate_price',
      render: (_, record) => {
        const applicationItem = getApplicationItem(record)
        return formatMoney(applicationItem?.estimate_field?.price || 0)
      },
    },
    {
      title: 'Ед.изм. смета',
      dataIndex: 'estimate_unit',
      key: 'estimate_unit',
      render: (_, record) => {
        const applicationItem = getApplicationItem(record)
        return applicationItem?.estimate_field?.unit
      },
    },
    {
      title: 'Кол-во смета',
      dataIndex: 'estimate_amount',
      key: 'estimate_amount',
      render: (_, record) => {
        const applicationItem = getApplicationItem(record)
        return <div style={numberCellStyle}>{formatDecimalAmount(applicationItem?.estimate_field?.amount)}</div>
      },
    },
    {
      title: 'Действия',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record) => {
        const applicationItem = getApplicationItem(record)
        return isProject ? (
          <Button disabled={!isProject} onClick={() => onUpdateHandler(applicationItem)} type="dashed">
            Редактировать
          </Button>
        ) : null
      },
    },
  ]

  useEffect(() => {
    const filtered = storedData
      .filter((item) => item?.display)
      .map((item) => {
        return columns.find((column) => column.key === item.key)
      })
    setFilteredColumns(filtered)
  }, [storedData, filter, isProject])

  return (
    <Table
      style={{ padding: '10px 0', marginLeft: -35 }}
      className={`export-table-${tableId}`}
      columns={filteredColumns}
      dataSource={applicationRecord?.implementation?.implementation_items}
      pagination={false}
      bordered
      sticky
      size="small"
      scroll={{
        x: 3500,
      }}
      summary={(pageData) => {
        const sum = pageData.reduce((acc, item) => {
          if (item.price && item.amount) {
            const finalAmount = calcSumAmount(item)
            return acc + item.price * finalAmount
          }
          return acc
        }, 0)

        const vatSum = pageData.reduce((acc, item) => {
          const finalAmount = calcSumAmount(item)
          if (item.vatType === 'up' && item.vatValue > 0 && item.price >= 0 && finalAmount >= 0) {
            const sum = item.price * finalAmount
            const vat = (sum / 100) * item.vatValue
            return sum + vat + acc
          } else if (item.price >= 0 && item.amount >= 0) {
            return acc + item.price * finalAmount
          }
          return acc
        }, 0)

        const transportationVatSum = applicationRecord?.transportation?.reduce((acc, item) => {
          const res =
            item.vatType === 'up' && item.vatValue > 0 ? item.price + (item?.price / 100) * item.vatValue : item.price
          return acc + res
        }, 0)

        const transportationSum = applicationRecord?.transportation?.reduce((acc, item) => {
          return acc + item.price
        }, 0)

        const sumColumnIndex = findIndex(filteredColumns, (item) => item.key === 'sum')
        const vatSumColumnIndex = findIndex(filteredColumns, (item) => item.key === 'vatSum')
        const vatTypeColumnIndex = findIndex(filteredColumns, (item) => item.key === 'vatType')
        const vatValueColumnIndex = findIndex(filteredColumns, (item) => item.key === 'vatValue')
        const vatCalcColumnIndex = findIndex(filteredColumns, (item) => item.key === 'vatCalc')
        const providerColumnIndex = findIndex(filteredColumns, (item) => item.key === 'provider')

        return (
          <Table.Summary>
            {applicationRecord?.transportation?.length > 0 &&
              applicationRecord?.transportation?.map((item) => {
                const vatTransportationSum =
                  item.vatType === 'up' && item.vatValue > 0
                    ? item.price + (item?.price / 100) * item.vatValue
                    : item.price

                let vatTransportation = ''
                if (item.vatType === 'up' && item.vatValue > 0 && item.price >= 0) {
                  vatTransportation = formatMoney((item.price / 100) * item.vatValue)
                } else if (item.vatType === 'include' && item.vatValue > 0 && item.price >= 0) {
                  vatTransportation = formatMoney((item.price * item.vatValue) / (100 + item.vatValue))
                }
                return (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>
                      <Text strong>Доставка:</Text>
                      <Text>{` из ${item.from} в ${item.to}`}</Text>
                    </Table.Summary.Cell>
                    {filteredColumns.map((filteredColumn, index) => {
                      if (index === 0) {
                        return null
                      }
                      if (index === sumColumnIndex) {
                        return (
                          <Table.Summary.Cell index={index}>
                            <Text>{formatMoney(item?.price)}</Text>
                          </Table.Summary.Cell>
                        )
                      }
                      if (index === vatSumColumnIndex) {
                        return (
                          <Table.Summary.Cell index={index}>
                            <Text>{formatMoney(vatTransportationSum)}</Text>
                          </Table.Summary.Cell>
                        )
                      }
                      if (index === vatTypeColumnIndex) {
                        return (
                          <Table.Summary.Cell index={index}>
                            <Text>{getVatType(item.vatType)}</Text>
                          </Table.Summary.Cell>
                        )
                      }
                      if (index === vatValueColumnIndex) {
                        return (
                          <Table.Summary.Cell index={index}>
                            <Text>{item.vatValue ? `${item.vatValue} %` : ''}</Text>
                          </Table.Summary.Cell>
                        )
                      }
                      if (index === vatCalcColumnIndex) {
                        return (
                          <Table.Summary.Cell index={index}>
                            <Text>{formatMoney(vatTransportation)}</Text>
                          </Table.Summary.Cell>
                        )
                      }
                      if (index === providerColumnIndex) {
                        return (
                          <Table.Summary.Cell index={index}>
                            <Text>{item.provider}</Text>
                          </Table.Summary.Cell>
                        )
                      }

                      return <Table.Summary.Cell index={index}></Table.Summary.Cell>
                    })}
                  </Table.Summary.Row>
                )
              })}
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>
                <Text strong>Итого:</Text>
              </Table.Summary.Cell>
              {filteredColumns.map((filteredColumn, index) => {
                if (index === 0) {
                  return null
                }
                if (index === sumColumnIndex) {
                  return (
                    <Table.Summary.Cell index={index}>
                      <Text strong>{formatMoney(sum + transportationSum)}</Text>
                    </Table.Summary.Cell>
                  )
                }
                if (index === vatSumColumnIndex) {
                  return (
                    <Table.Summary.Cell index={index}>
                      <Text strong>{formatMoney(vatSum + transportationVatSum)}</Text>
                    </Table.Summary.Cell>
                  )
                }

                return <Table.Summary.Cell index={index}></Table.Summary.Cell>
              })}
            </Table.Summary.Row>
          </Table.Summary>
        )
      }}
    />
  )
}

export default ImplementationItemsTable
